import type { AxiosRequestConfig } from "axios";
import axios from "axios";

const tokenPromise = {
  resolve: undefined as undefined | ((callback: () => Promise<string>) => void),
};
const tokenContainer = {
  getAccessTokenCallback: new Promise<() => Promise<string>>((resolve) => {
    tokenPromise.resolve = resolve;
  }),
};

export function setAccessTokenCallback(callback: () => Promise<string>) {
  if (tokenPromise.resolve) {
    tokenPromise.resolve(callback);
  }
}

async function getAccessToken() {
  const callback = await tokenContainer.getAccessTokenCallback;
  const token = await callback();
  return token;
}

// certain types we don't want to try and sanitize
const PRESERVE_TYPES = [Date, ArrayBuffer, Set, Map];

if (typeof globalThis.FormData !== "undefined") {
  PRESERVE_TYPES.push(globalThis.FormData);
}

if (typeof globalThis.File !== "undefined") {
  PRESERVE_TYPES.push(globalThis.File);
}

if (typeof globalThis.Blob !== "undefined") {
  PRESERVE_TYPES.push(globalThis.Blob);
}

function isPlainType(value: any): boolean {
  if (!value || typeof value !== "object") return false;
  if (PRESERVE_TYPES.some((Type) => value instanceof Type)) return false;
  return Object.getPrototypeOf(value) === Object.prototype;
}

// Sanitize payload: trim strings, recursively handle arrays/objects
function sanitizePayload<T>(data: T): T {
  if (typeof data === "string") {
    const trimmed = data.trim();
    return (trimmed === "" ? null : trimmed) as unknown as T;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizePayload) as unknown as T;
  }

  if (data && typeof data === "object") {
    if (!isPlainType(data)) {
      return data;
    }

    const result: Record<string, any> = {};
    for (const key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
      const value = (data as any)[key];
      const sanitized = sanitizePayload(value);

      result[key] = sanitized;
    }
    return result as T;
  }

  return data;
}

// mutator pulled from Orval example: https://github.com/orval-labs/orval/blob/master/samples/react-query/basic/src/api/mutator/custom-instance.ts
// Without this, all requests would return a full Axios response object instead of just the data.
export const AXIOS_INSTANCE = axios.create({
  baseURL:
    import.meta.env.VITE_PUBLIC_BASE_URL || "http://localhost:8080/atlas",
});

// Add request interceptor to attach auth token to all requests
AXIOS_INSTANCE.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Sanitize request payload (data, params)
  if (config.data) {
    config.data = sanitizePayload(config.data);
  }
  if (config.params) {
    config.params = sanitizePayload(config.params);
  }

  return config;
});

export const axiosMutator = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const promise = AXIOS_INSTANCE({ ...config, ...options }).then(
    ({ data }) => data,
  );

  return promise;
};
